import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IMetaSettings, OmniCampaign } from '@feathr/blackbox';
import { Alert, UniversalSelect } from '@feathr/components';
import { TooltipOption } from '@feathr/extender/components/SelectOptions';

import * as styles from './MetaAdCategorySelect.css';

export interface IAdCategory {
  id: IMetaSettings['special_ad_category'];
  isDisabled: boolean;
  name: string;
  description: string;
  tooltip?: string;
}

interface IProps {
  campaign: OmniCampaign;
}

function MetaAdCategorySelect({ campaign }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const metaSettings: Partial<IMetaSettings> = campaign.get('meta_settings') || {};

  function handleSelect(option: IAdCategory): void {
    campaign.set({
      meta_settings: {
        ...metaSettings,
        special_ad_category: option.id,
      } as IMetaSettings,
    });
  }

  const options: IAdCategory[] = useMemo(
    () => [
      {
        id: 'NONE',
        isDisabled: false,
        name: t('None'),
        description: t(
          'If your ads do not relate to any of the other topical categories, choose this option.',
        ),
      },
      {
        id: 'HOUSING',
        isDisabled: false,
        name: t('Housing'),
        description: t(
          'Ads promoting or related to the sale or lease of property, apartments, etc.',
        ),
      },
      {
        id: 'FINANCIAL_PRODUCTS_SERVICES',
        isDisabled: false,
        name: t('Financial products and services'),
        description: t('Ads promoting or related to financial services and products.'),
      },
      {
        id: 'EMPLOYMENT',
        isDisabled: false,
        name: t('Employment'),
        description: t('Ads promoting or related to job opportunities.'),
      },
    ],
    [t],
  );

  return (
    <>
      <UniversalSelect<IAdCategory>
        components={{ Option: TooltipOption }}
        getOptionLabel={(option: IAdCategory): string => option.name}
        getOptionValue={(option: IAdCategory): string => option.id}
        helpText={t(
          'Indicate if your ad belongs to a special category to prevent discriminatory practices.',
        )}
        label={t('Special Ad Category')}
        name={'ad_category_select'}
        onSelectSingle={handleSelect}
        options={options}
        placeholder={t('Select a category')}
        required={true}
        value={options.find((option) => option.id === metaSettings.special_ad_category)}
      />
      {campaign.hasSpecialAdCategory && (
        <Alert
          className={styles.flushAlert}
          description={t(
            'For your selected Special Ad Category, Meta requires broader location targeting than zip codes. To ensure your ads are delivered successfully, zip codes for Meta targeting will automatically expand to their corresponding cities. Display targeting remains unaffected and will continue to support zip code targeting.',
          )}
          name={'special_ad_category_geo_notice'}
          title={t('Zip codes expand to cities for Meta')}
          type={'warning'}
        />
      )}
    </>
  );
}

export default observer(MetaAdCategorySelect);
